import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import {
    Box,
    Tab,
    Tabs,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import AccountTreeIcon from '@material-ui/icons/AccountTree'
import { toast } from 'react-toastify'

import { useUser } from '../../hooks/useUser'
import { Routes } from '../../navigation/config/routes'
import type { WorkbookSectionsState } from '../PipelineFlowDemo/workbookSectionDraft'
import {
    definitionToSectionsState,
    sectionsStateToDefinition,
    useCopyPropertyApplicationWorkbook,
    useCreatePropertyApplicationWorkbook,
    useEzosWorkbookFields,
    usePropertyApplicationWorkbook,
    usePropertyApplicationWorkbooks,
    useSavePropertyApplicationWorkbook,
} from '../ApplicationWorkbook'
import {
    useCopyPropertyPipelineConfig,
    usePropertyPipelineConfig,
} from '../LeasingSeason/Configurations/PipelineBuilder/usePipelineConfigApi'
import { ApplicantDataSetupTab } from './ApplicantDataSetupTab'
import { ApplicationFeeTab } from './ApplicationFeeTab'
import { ConditionalApprovalsSetupTab } from './ConditionalApprovalsSetupTab'
import { PropertyDataSetupTab } from './PropertyDataSetupTab'
import {
    ConditionalApprovalsPolicy,
    applyQualifyingMethodOptionsToSections,
    defaultConditionalApprovalsPolicy,
} from './conditionalApprovals'
import { EditScopeBanner } from './EditScopeBanner'
import type { FormSaveAction } from './FormSaveButton'
import {
    GuestCardChannelBindings,
    GuestCardPackTab,
    channelBindingsToConfigured,
    emptyGuestCardChannelBindings,
} from './GuestCardPackTab'
import { LeadsPipelineComposeTab } from './LeadsPipelineComposeTab'
import { NameConfigDialog } from './NameConfigDialog'
import { SeasonConfigBundleBar } from './SeasonConfigBundleBar'
import { SeasonConfigBundleChooser } from './SeasonConfigBundleChooser'
import { SignableDocsSetTab } from './SignableDocsSetTab'
import {
    LEASING_SEASON_CONFIG_CONTENT_MAX_WIDTH,
    PropertyConfigCatalog,
    SelectedSetupConfig,
    createInitialPropertyCatalog,
} from './setupArtifacts'
import {
    LeasePacketMember,
    emptyGuarantorPacket,
    emptyLeasePacket,
} from './signableDocRoles'
import {
    SeasonConfigBundle,
    SeasonConfigSetupSlotId,
    SeasonConfigSlotId,
    SEASON_CONFIG_SLOT_META,
    artifactUsedByOthers,
    buildApiDefinition,
    bundleFromApiDetail,
    ensureCatalogHas,
    forkSetupSlot,
    conditionalPolicyFromPackDefinition,
    guestCardChannelBindingsFromPackDefinition,
    guestCardPackDefinitionFromChannels,
    guarantorPacketFromPackDefinition,
    leasePacketFromPackDefinition,
    listItemToBundleOption,
    mergePackCatalog,
    signableDocSetDefinitionFromPackets,
} from './seasonConfigBundle'
import {
    emptyPropertyDataDraft,
    propertyDataFromPackDefinition,
    propertyDataToPackDefinition,
    PropertyDataDraft,
} from './propertyDataPack'
import {
    PropertyLeasingSeasonConfigDetail,
    useCopyPropertyLeasingSeasonConfig,
    useCreatePropertyLeasingSeasonConfig,
    usePropertyLeasingSeasonConfig,
    usePropertyLeasingSeasonConfigs,
    useSavePropertyLeasingSeasonConfig,
} from './useLeasingSeasonConfigApi'
import {
    parseSetupPackId,
    useConditionalApprovalsList,
    useCopySetupPack,
    useCreateConditionalApprovals,
    useCreateFeePolicy,
    useCreateGuestCardPack,
    useCreatePropertyDataPack,
    useCreateSignableDocSet,
    useFeePolicies,
    useGuestCardPacks,
    usePropertyDataPacks,
    useSaveConditionalApprovals,
    useSaveFeePolicy,
    useSaveGuestCardPack,
    useSavePropertyDataPack,
    useSaveSignableDocSet,
    useSignableDocSets,
} from './useSetupPacksApi'

/**
 * Flowchart Compose UI lives in `../PipelineFlowDemo` (PipelineFlowDemoTab).
 * Kept in the repo for internal / EZOS use; Compose mounts LeadsPipelineComposeTab.
 */

type SetupTabId = SeasonConfigSetupSlotId

type ShellArea = 'setup' | 'compose'

type PackExtrasOverride = {
    guestCardChannels?: GuestCardChannelBindings
    conditionalPolicy?: ConditionalApprovalsPolicy
    leasePacket?: LeasePacketMember[]
    guarantorPacket?: LeasePacketMember[]
    workbookSections?: WorkbookSectionsState
    feeAmount?: string
    feeCurrency?: string
    propertyDataDraft?: PropertyDataDraft
}

const DISCARD_UNSAVED = 'You have unsaved changes. Discard them?'

function packSaveAction(
    enabled: boolean,
    dirty: boolean,
    saving: boolean,
    onSave: () => void,
): FormSaveAction | undefined {
    if (!enabled) return undefined
    return { dirty, saving, onSave }
}

const SETUP_TABS: { id: SetupTabId; label: string }[] = [
    { id: 'conditional_approvals', label: 'Qualifying Methods' },
    { id: 'applicant_data', label: 'Applicant Data' },
    { id: 'guest_card', label: 'Guest Card' },
    { id: 'application_fee', label: 'Application Fee' },
    { id: 'property_data', label: 'Property Data' },
    { id: 'signable_docs', label: 'Signable Documents' },
]

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        width: '100%',
        maxWidth: 'none',
        margin: 0,
        padding: theme.spacing(1, 1),
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px)',
        overflow: 'hidden',
        boxSizing: 'border-box',
        gap: theme.spacing(0.75),
    },
    header: {
        flexShrink: 0,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
    },
    titleRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        minWidth: 0,
        flex: 1,
    },
    titleIcon: {
        width: 32,
        height: 32,
        borderRadius: theme.spacing(0.75),
        backgroundColor: theme.palette.primary.main + '14',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: theme.palette.primary.main,
        flexShrink: 0,
        marginTop: 2,
    },
    titleBlock: {
        minWidth: 0,
        flex: 1,
    },
    pageTitle: {
        fontWeight: 700,
        fontSize: '1.25rem',
        lineHeight: 1.3,
    },
    pageSubtitle: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        marginTop: 2,
        lineHeight: 1.4,
    },
    headerBundle: {
        minWidth: 0,
        flex: 1,
    },
    navRegions: {
        flexShrink: 0,
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: theme.spacing(1),
        width: '100%',
        maxWidth: LEASING_SEASON_CONFIG_CONTENT_MAX_WIDTH,
        alignSelf: 'center',
        boxSizing: 'border-box',
    },
    region: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden',
    },
    regionSetup: {
        flex: '1 1 auto',
        minWidth: 0,
    },
    regionCompose: {
        flex: '0 0 auto',
    },
    regionLabel: {
        fontSize: '0.6875rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: theme.palette.text.secondary,
        padding: theme.spacing(0.5, 1.25, 0),
    },
    regionTabs: {
        minHeight: 40,
        '& .MuiTabs-indicator': {
            height: 2,
        },
        '& .MuiTab-root': {
            minHeight: 40,
            minWidth: 0,
            padding: theme.spacing(1, 1.5),
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
        },
    },
    regionTabsCompose: {
        '& .MuiTab-root': {
            minWidth: 0,
            paddingLeft: theme.spacing(1.75),
            paddingRight: theme.spacing(1.75),
        },
    },
    regionActive: {
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 1px ${theme.palette.primary.main}33`,
    },
    tabLabel: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
    },
    tabBadge: {
        minWidth: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: theme.palette.warning.main,
        flexShrink: 0,
    },
    tabPanel: {
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    tabContent: {
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    centered: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(3),
        height: '100%',
    },
}))

function editorPath(bundleId: number | string): string {
    return Routes.manage.leasingSeasonConfig.detail.replace(
        ':id',
        String(bundleId),
    )
}

/**
 * Leasing Season Config shell — top-level bundle of six artifact slots.
 * Seasons will apply one bundle; Setup/Compose edit the pinned slot artifacts.
 */
export const LeasingSeasonConfigEditorPage: React.FC = () => {
    const classes = useStyles()
    const history = useHistory()
    const { id } = useParams<{ id?: string }>()
    const bundleId =
        id != null && id !== '' && Number.isFinite(Number(id))
            ? Number(id)
            : null
    const { workspaceUser } = useUser()
    const apartmentId = workspaceUser?.active_workspace?.id

    const listQuery = usePropertyLeasingSeasonConfigs(apartmentId)
    const detailQuery = usePropertyLeasingSeasonConfig(apartmentId, bundleId)
    const createMutation = useCreatePropertyLeasingSeasonConfig(apartmentId)
    const copyMutation = useCopyPropertyLeasingSeasonConfig(apartmentId)
    const saveMutation = useSavePropertyLeasingSeasonConfig(
        apartmentId,
        bundleId,
    )
    const [copyDialogOpen, setCopyDialogOpen] = useState(false)
    const [artifactCopy, setArtifactCopy] = useState<{
        slot: SeasonConfigSlotId
        initialName: string
        label: string
    } | null>(null)
    const guestPacksQuery = useGuestCardPacks(apartmentId)
    const signableSetsQuery = useSignableDocSets(apartmentId)
    const feePoliciesQuery = useFeePolicies(apartmentId)
    const conditionalListQuery = useConditionalApprovalsList(apartmentId)
    const propertyDataPacksQuery = usePropertyDataPacks(apartmentId)
    const saveGuestCardPack = useSaveGuestCardPack(apartmentId, bundleId)
    const saveSignableDocSet = useSaveSignableDocSet(apartmentId, bundleId)
    const saveFeePolicy = useSaveFeePolicy(apartmentId, bundleId)
    const saveConditionalApprovals = useSaveConditionalApprovals(
        apartmentId,
        bundleId,
    )
    const savePropertyDataPack = useSavePropertyDataPack(apartmentId, bundleId)
    const createGuestCardPack = useCreateGuestCardPack(apartmentId)
    const createSignableDocSet = useCreateSignableDocSet(apartmentId)
    const createFeePolicy = useCreateFeePolicy(apartmentId)
    const createConditionalApprovals = useCreateConditionalApprovals(
        apartmentId,
    )
    const createPropertyDataPack = useCreatePropertyDataPack(apartmentId)
    const createApplicantWorkbook = useCreatePropertyApplicationWorkbook(
        apartmentId,
    )
    const copySetupPack = useCopySetupPack(apartmentId)
    const copyApplicantWorkbook = useCopyPropertyApplicationWorkbook(
        apartmentId,
    )
    const copyPipelineConfig = useCopyPropertyPipelineConfig(apartmentId)
    const workbooksQuery = usePropertyApplicationWorkbooks(apartmentId)
    const ezosFieldsQuery = useEzosWorkbookFields(true)

    const [bundle, setBundle] = useState<SeasonConfigBundle | null>(null)
    const [packDirty, setPackDirty] = useState<
        Partial<Record<SetupTabId, boolean>>
    >({})
    const packDirtyRef = useRef(packDirty)
    packDirtyRef.current = packDirty
    const [timelineDirty, setTimelineDirty] = useState(false)
    const hasUnsavedRef = useRef(false)
    hasUnsavedRef.current =
        Object.values(packDirty).some(Boolean) || timelineDirty

    const [shellArea, setShellArea] = useState<ShellArea>('setup')
    const [setupTab, setSetupTab] = useState<SetupTabId>(
        'conditional_approvals',
    )

    const [
        applicantSelected,
        setApplicantSelected,
    ] = useState<SelectedSetupConfig | null>(null)
    const [
        guestCardSelected,
        setGuestCardSelected,
    ] = useState<SelectedSetupConfig | null>(null)
    const [feeSelected, setFeeSelected] = useState<SelectedSetupConfig | null>(
        null,
    )
    const [
        conditionalSelected,
        setConditionalSelected,
    ] = useState<SelectedSetupConfig | null>(null)
    const [
        signableSelected,
        setSignableSelected,
    ] = useState<SelectedSetupConfig | null>(null)
    const [
        propertyDataSelected,
        setPropertyDataSelected,
    ] = useState<SelectedSetupConfig | null>(null)
    const [pipelineConfigId, setPipelineConfigId] = useState<number | null>(
        null,
    )

    const applicantWorkbookId = parseSetupPackId(applicantSelected?.id)
    const applicantWorkbookQuery = usePropertyApplicationWorkbook(
        apartmentId,
        applicantWorkbookId,
    )
    const pipelineQuery = usePropertyPipelineConfig(
        apartmentId,
        pipelineConfigId,
    )
    const saveApplicantWorkbook = useSavePropertyApplicationWorkbook(
        apartmentId,
        applicantWorkbookId,
    )

    const [
        propertyCatalog,
        setPropertyCatalog,
    ] = useState<PropertyConfigCatalog>(() => createInitialPropertyCatalog())

    const setExistingFor = useCallback(
        (kind: keyof PropertyConfigCatalog) => (
            next: PropertyConfigCatalog[typeof kind],
        ) => {
            setPropertyCatalog((prev) => ({ ...prev, [kind]: next }))
        },
        [],
    )

    const [
        applicantSections,
        setApplicantSections,
    ] = useState<WorkbookSectionsState>({})
    const [
        conditionalPolicy,
        setConditionalPolicy,
    ] = useState<ConditionalApprovalsPolicy>(() =>
        defaultConditionalApprovalsPolicy(),
    )

    const [
        guestCardChannels,
        setGuestCardChannels,
    ] = useState<GuestCardChannelBindings>(() =>
        emptyGuestCardChannelBindings(),
    )
    const [leasePacket, setLeasePacket] = useState<LeasePacketMember[]>(() =>
        emptyLeasePacket(),
    )
    const [guarantorPacket, setGuarantorPacket] = useState<LeasePacketMember[]>(
        () => emptyGuarantorPacket(),
    )
    const [feeAmount, setFeeAmount] = useState('50.00')
    const [feeCurrency, setFeeCurrency] = useState('USD')
    const [
        propertyDataDraft,
        setPropertyDataDraft,
    ] = useState<PropertyDataDraft>(() => emptyPropertyDataDraft())

    const applyDetail = useCallback(
        (detail: PropertyLeasingSeasonConfigDetail, pushUrl = true) => {
            const { bundle: next, extras } = bundleFromApiDetail(detail)
            setPackDirty({})
            setTimelineDirty(false)
            setBundle(next)
            setApplicantSelected(next.slots.applicant_data)
            setGuestCardSelected(next.slots.guest_card)
            setFeeSelected(next.slots.application_fee)
            setConditionalSelected(next.slots.conditional_approvals)
            setSignableSelected(next.slots.signable_docs)
            setPropertyDataSelected(next.slots.property_data)
            setPipelineConfigId(next.slots.leads_pipeline.configId)
            setLeasePacket(extras.leasePacket)
            setGuarantorPacket(extras.guarantorPacket)
            setConditionalPolicy(extras.conditionalPolicy)
            setApplicantSections(extras.workbookSections)
            setGuestCardChannels(
                extras.guestCardChannels ?? emptyGuestCardChannelBindings(),
            )
            setFeeAmount(detail.fee_policy?.amount ?? '50.00')
            setFeeCurrency(detail.fee_policy?.currency ?? 'USD')
            setPropertyDataDraft(
                propertyDataFromPackDefinition(
                    detail.property_data_pack?.definition,
                ),
            )

            setPropertyCatalog((prev) => ({
                applicant_data: ensureCatalogHas(
                    prev.applicant_data,
                    next.slots.applicant_data,
                ),
                guest_card: ensureCatalogHas(
                    prev.guest_card,
                    next.slots.guest_card,
                ),
                application_fee: ensureCatalogHas(
                    prev.application_fee,
                    next.slots.application_fee,
                ),
                conditional_approvals: ensureCatalogHas(
                    prev.conditional_approvals,
                    next.slots.conditional_approvals,
                ),
                signable_docs: ensureCatalogHas(
                    prev.signable_docs,
                    next.slots.signable_docs,
                ),
                property_data: ensureCatalogHas(
                    prev.property_data,
                    next.slots.property_data,
                ),
            }))

            if (pushUrl) {
                history.push(editorPath(detail.id))
            }
            setShellArea('setup')
            setSetupTab('conditional_approvals')
        },
        [history],
    )

    useEffect(() => {
        if (bundleId == null) {
            setBundle(null)
            return
        }
        if (detailQuery.data && String(detailQuery.data.id) !== bundle?.id) {
            applyDetail(detailQuery.data, false)
        }
    }, [bundleId, detailQuery.data, bundle?.id, applyDetail])

    useEffect(() => {
        if (guestPacksQuery.data) {
            setPropertyCatalog((prev) => ({
                ...prev,
                guest_card: mergePackCatalog(
                    prev.guest_card,
                    guestPacksQuery.data,
                ),
            }))
        }
    }, [guestPacksQuery.data])

    useEffect(() => {
        if (signableSetsQuery.data) {
            setPropertyCatalog((prev) => ({
                ...prev,
                signable_docs: mergePackCatalog(
                    prev.signable_docs,
                    signableSetsQuery.data,
                ),
            }))
        }
    }, [signableSetsQuery.data])

    useEffect(() => {
        if (feePoliciesQuery.data) {
            setPropertyCatalog((prev) => ({
                ...prev,
                application_fee: mergePackCatalog(
                    prev.application_fee,
                    feePoliciesQuery.data,
                ),
            }))
        }
    }, [feePoliciesQuery.data])

    useEffect(() => {
        if (conditionalListQuery.data) {
            setPropertyCatalog((prev) => ({
                ...prev,
                conditional_approvals: mergePackCatalog(
                    prev.conditional_approvals,
                    conditionalListQuery.data,
                ),
            }))
        }
    }, [conditionalListQuery.data])

    useEffect(() => {
        if (propertyDataPacksQuery.data) {
            setPropertyCatalog((prev) => ({
                ...prev,
                property_data: mergePackCatalog(
                    prev.property_data,
                    propertyDataPacksQuery.data,
                ),
            }))
        }
    }, [propertyDataPacksQuery.data])

    useEffect(() => {
        if (workbooksQuery.data) {
            setPropertyCatalog((prev) => ({
                ...prev,
                applicant_data: mergePackCatalog(
                    prev.applicant_data,
                    workbooksQuery.data.map((row) => ({
                        id: row.id,
                        name: row.name,
                        description: '',
                        modified_date: row.modified_date,
                    })),
                ),
            }))
        }
    }, [workbooksQuery.data])

    useEffect(() => {
        if (!applicantWorkbookQuery.data?.definition) return
        if (packDirtyRef.current.applicant_data) return
        const mapped = definitionToSectionsState(
            applicantWorkbookQuery.data.definition,
            ezosFieldsQuery.data ?? [],
        )
        setApplicantSections(
            applyQualifyingMethodOptionsToSections(mapped, conditionalPolicy),
        )
        // conditionalPolicy intentionally omitted: policy edits sync via
        // onPolicyChange; including it here would reload the workbook draft.
    }, [
        applicantWorkbookQuery.data?.definition,
        applicantWorkbookQuery.data?.id,
        ezosFieldsQuery.data,
    ])

    const persistNow = useCallback(
        async (
            nextBundle: SeasonConfigBundle,
            extrasOverride?: PackExtrasOverride,
            onSaved?: () => void,
        ) => {
            if (bundleId == null || apartmentId == null) return
            const seasonLocksConfig = detailQuery.data?.is_editable === false
            // Active-season lock: do not mutate the bound config or its packs.
            if (seasonLocksConfig) {
                return
            }
            const channels =
                extrasOverride?.guestCardChannels ?? guestCardChannels
            const extras = {
                guestCardMembers: channelBindingsToConfigured(channels),
                guestCardChannels: channels,
                conditionalPolicy:
                    extrasOverride?.conditionalPolicy ?? conditionalPolicy,
                leasePacket: extrasOverride?.leasePacket ?? leasePacket,
                guarantorPacket:
                    extrasOverride?.guarantorPacket ?? guarantorPacket,
                workbookSections:
                    extrasOverride?.workbookSections ?? applicantSections,
                feeAmount: extrasOverride?.feeAmount ?? feeAmount,
                feeCurrency: extrasOverride?.feeCurrency ?? feeCurrency,
                propertyDataDraft:
                    extrasOverride?.propertyDataDraft ?? propertyDataDraft,
            }
            const patchGuest = extrasOverride?.guestCardChannels != null
            const patchConditional = extrasOverride?.conditionalPolicy != null
            const patchSignable =
                extrasOverride?.leasePacket != null ||
                extrasOverride?.guarantorPacket != null
            const patchFee =
                extrasOverride?.feeAmount != null ||
                extrasOverride?.feeCurrency != null
            const patchPropertyData = extrasOverride?.propertyDataDraft != null
            const patchWorkbook = extrasOverride?.workbookSections != null
            // Slot / edit-scope changes always PATCH the bundle. Pack-member
            // extras (guest cards, fees, …) live on the pack rows — when the
            // bundle is locked by an active season, skip the config PATCH so
            // those pack saves don't spam 409 toasts.
            const extrasOnly = extrasOverride != null
            const shouldPatchConfig = !extrasOnly || !seasonLocksConfig

            const tasks: Promise<unknown>[] = []

            if (shouldPatchConfig) {
                const definition = buildApiDefinition(
                    nextBundle.slots,
                    nextBundle.editScopeBySlot,
                    extras,
                )
                tasks.push(
                    saveMutation.mutateAsync(
                        { definition },
                        {
                            onError: (err: unknown) => {
                                const detail = (err as {
                                    response?: {
                                        data?: { detail?: string }
                                    }
                                })?.response?.data?.detail
                                toast.error(
                                    typeof detail === 'string' && detail
                                        ? detail
                                        : 'Could not save leasing season config.',
                                )
                            },
                        },
                    ),
                )
            }

            if (patchWorkbook && applicantWorkbookId != null) {
                tasks.push(
                    saveApplicantWorkbook.mutateAsync(
                        sectionsStateToDefinition(extras.workbookSections),
                        {
                            onError: () => {
                                toast.error('Could not save applicant data.')
                            },
                        },
                    ),
                )
            }

            const guestPackId = parseSetupPackId(
                nextBundle.slots.guest_card?.id,
            )
            if (patchGuest && guestPackId != null) {
                tasks.push(
                    saveGuestCardPack.mutateAsync(
                        {
                            packId: guestPackId,
                            definition: guestCardPackDefinitionFromChannels(
                                channels,
                            ),
                        },
                        {
                            onError: () => {
                                toast.error('Could not save guest card pack.')
                            },
                        },
                    ),
                )
            }

            const signableSetId = parseSetupPackId(
                nextBundle.slots.signable_docs?.id,
            )
            if (patchSignable && signableSetId != null) {
                tasks.push(
                    saveSignableDocSet.mutateAsync(
                        {
                            packId: signableSetId,
                            definition: signableDocSetDefinitionFromPackets(
                                extras.leasePacket,
                                extras.guarantorPacket,
                            ),
                        },
                        {
                            onError: () => {
                                toast.error('Could not save signable doc set.')
                            },
                        },
                    ),
                )
            }

            const feePolicyId = parseSetupPackId(
                nextBundle.slots.application_fee?.id,
            )
            if (patchFee && feePolicyId != null) {
                tasks.push(
                    saveFeePolicy.mutateAsync(
                        {
                            packId: feePolicyId,
                            amount: extras.feeAmount,
                            currency: extras.feeCurrency,
                        },
                        {
                            onError: () => {
                                toast.error('Could not save fee policy.')
                            },
                        },
                    ),
                )
            }

            const propertyDataPackId = parseSetupPackId(
                nextBundle.slots.property_data?.id,
            )
            if (patchPropertyData && propertyDataPackId != null) {
                tasks.push(
                    savePropertyDataPack.mutateAsync(
                        {
                            packId: propertyDataPackId,
                            definition: propertyDataToPackDefinition(
                                extras.propertyDataDraft,
                            ),
                        },
                        {
                            onError: () => {
                                toast.error('Could not save property data.')
                            },
                        },
                    ),
                )
            }

            const conditionalId = parseSetupPackId(
                nextBundle.slots.conditional_approvals?.id,
            )
            if (patchConditional && conditionalId != null) {
                tasks.push(
                    saveConditionalApprovals.mutateAsync(
                        {
                            packId: conditionalId,
                            definition: (extras.conditionalPolicy as unknown) as Record<
                                string,
                                unknown
                            >,
                        },
                        {
                            onError: () => {
                                toast.error(
                                    'Could not save qualifying methods.',
                                )
                            },
                        },
                    ),
                )
            }

            if (tasks.length === 0) return
            try {
                await Promise.all(tasks)
                onSaved?.()
            } catch {
                // Per-mutation onError toasts already fired.
            }
        },
        [
            apartmentId,
            applicantSections,
            applicantWorkbookId,
            bundleId,
            conditionalPolicy,
            detailQuery.data?.is_editable,
            feeAmount,
            feeCurrency,
            guestCardChannels,
            propertyDataDraft,
            saveApplicantWorkbook,
            saveConditionalApprovals,
            saveFeePolicy,
            saveGuestCardPack,
            saveMutation,
            savePropertyDataPack,
            saveSignableDocSet,
            leasePacket,
            guarantorPacket,
        ],
    )

    const syncBundleSlots = useCallback(
        (patch: Partial<SeasonConfigBundle['slots']>) => {
            setBundle((prev) => {
                if (!prev) return prev
                const next = {
                    ...prev,
                    modifiedAt: new Date().toISOString(),
                    slots: { ...prev.slots, ...patch },
                }
                void persistNow(next)
                return next
            })
        },
        [persistNow],
    )

    const clearBundle = useCallback(() => {
        if (hasUnsavedRef.current && !window.confirm(DISCARD_UNSAVED)) {
            return
        }
        setPackDirty({})
        setTimelineDirty(false)
        setBundle(null)
        history.push(Routes.manage.leasingSeasonConfig.root)
    }, [history])

    const selectPipeline = useCallback(
        (nextId: number) => {
            setPipelineConfigId(nextId)
            syncBundleSlots({
                leads_pipeline: {
                    configId: nextId,
                    name: `Pipeline #${nextId}`,
                },
            })
        },
        [syncBundleSlots],
    )

    const clearPipeline = useCallback(() => {
        setPipelineConfigId(null)
        syncBundleSlots({
            leads_pipeline: { configId: null, name: null },
        })
    }, [syncBundleSlots])

    const existingOptions = useMemo(
        () => (listQuery.data ?? []).map(listItemToBundleOption),
        [listQuery.data],
    )

    const currentSetupSelected = (
        tab: SetupTabId,
    ): SelectedSetupConfig | null => {
        switch (tab) {
            case 'applicant_data':
                return applicantSelected
            case 'guest_card':
                return guestCardSelected
            case 'application_fee':
                return feeSelected
            case 'conditional_approvals':
                return conditionalSelected
            case 'signable_docs':
                return signableSelected
            case 'property_data':
                return propertyDataSelected
        }
    }

    const currentSlots = useCallback(
        (): SeasonConfigBundle['slots'] => ({
            applicant_data: applicantSelected,
            guest_card: guestCardSelected,
            application_fee: feeSelected,
            conditional_approvals: conditionalSelected,
            signable_docs: signableSelected,
            property_data: propertyDataSelected,
            leads_pipeline: {
                configId: pipelineConfigId,
                name:
                    pipelineConfigId != null
                        ? bundle?.slots.leads_pipeline.name ??
                          `Pipeline #${pipelineConfigId}`
                        : null,
                orgMandated: bundle?.slots.leads_pipeline.orgMandated,
                usedByOtherConfigs:
                    bundle?.slots.leads_pipeline.usedByOtherConfigs,
            },
        }),
        [
            applicantSelected,
            bundle?.slots.leads_pipeline.name,
            bundle?.slots.leads_pipeline.orgMandated,
            bundle?.slots.leads_pipeline.usedByOtherConfigs,
            conditionalSelected,
            feeSelected,
            guestCardSelected,
            pipelineConfigId,
            propertyDataSelected,
            signableSelected,
        ],
    )

    const persistExtras = useCallback(
        (extrasOverride?: PackExtrasOverride, onSaved?: () => void) => {
            if (!bundle) return
            void persistNow(
                {
                    ...bundle,
                    slots: currentSlots(),
                    modifiedAt: new Date().toISOString(),
                },
                extrasOverride,
                onSaved,
            )
        },
        [bundle, currentSlots, persistNow],
    )

    const markPackDirty = (tab: SetupTabId) => {
        setPackDirty((prev) => (prev[tab] ? prev : { ...prev, [tab]: true }))
    }

    const clearPackDirty = (tab: SetupTabId) => {
        setPackDirty((prev) => (prev[tab] ? { ...prev, [tab]: false } : prev))
    }

    const confirmDiscardPack = (tab: SetupTabId): boolean => {
        if (!packDirty[tab]) return true
        if (!window.confirm(DISCARD_UNSAVED)) return false
        clearPackDirty(tab)
        return true
    }

    useEffect(() => {
        const dirty = Object.values(packDirty).some(Boolean) || timelineDirty
        if (!dirty) return
        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault()
            event.returnValue = ''
        }
        window.addEventListener('beforeunload', onBeforeUnload)
        return () => window.removeEventListener('beforeunload', onBeforeUnload)
    }, [packDirty, timelineDirty])

    const setSetupSelected = (
        tab: SetupTabId,
        config: SelectedSetupConfig | null,
    ) => {
        clearPackDirty(tab)
        switch (tab) {
            case 'applicant_data':
                setApplicantSelected(config)
                syncBundleSlots({ applicant_data: config })
                break
            case 'guest_card':
                setGuestCardSelected(config)
                syncBundleSlots({ guest_card: config })
                break
            case 'application_fee':
                setFeeSelected(config)
                syncBundleSlots({ application_fee: config })
                break
            case 'conditional_approvals':
                setConditionalSelected(config)
                syncBundleSlots({ conditional_approvals: config })
                break
            case 'signable_docs':
                setSignableSelected(config)
                syncBundleSlots({ signable_docs: config })
                break
            case 'property_data':
                setPropertyDataSelected(config)
                syncBundleSlots({ property_data: config })
                break
        }
    }

    const pinCreatedPack = (
        tab: SetupTabId,
        previous: SelectedSetupConfig,
        created: {
            id: number
            name: string
            description?: string
            modified_date?: string
        },
    ): SelectedSetupConfig => {
        const selected: SelectedSetupConfig = {
            ...previous,
            id: String(created.id),
            name: created.name,
            source: 'existing',
        }
        setPropertyCatalog((prev) => ({
            ...prev,
            [tab]: mergePackCatalog(
                prev[tab].filter((row) => row.id !== previous.id),
                [created],
            ),
        }))
        setSetupSelected(tab, selected)
        return selected
    }

    const copyNameFor = (name: string) => `${name} (copy)`

    const openArtifactCopy = (slot: SeasonConfigSlotId) => {
        if (!bundle) return
        if (slot === 'leads_pipeline') {
            if (pipelineConfigId == null) return
            const currentName =
                bundle.slots.leads_pipeline.name ||
                `Pipeline #${pipelineConfigId}`
            setArtifactCopy({
                slot,
                initialName: copyNameFor(currentName),
                label: SEASON_CONFIG_SLOT_META.leads_pipeline.label,
            })
            return
        }
        const selected = currentSetupSelected(slot)
        if (!selected) return
        setArtifactCopy({
            slot,
            initialName: copyNameFor(selected.name),
            label: SEASON_CONFIG_SLOT_META[slot].label,
        })
    }

    const copyPinnedArtifact = async (name: string) => {
        if (!artifactCopy || !bundle) return
        const slot = artifactCopy.slot
        const trimmed = name.trim()
        if (!trimmed) return

        const pathByTab: Partial<
            Record<
                SetupTabId,
                | 'guest-card-packs'
                | 'signable-doc-sets'
                | 'fee-policies'
                | 'conditional-approvals'
                | 'property-data-packs'
            >
        > = {
            guest_card: 'guest-card-packs',
            signable_docs: 'signable-doc-sets',
            application_fee: 'fee-policies',
            conditional_approvals: 'conditional-approvals',
            property_data: 'property-data-packs',
        }

        try {
            if (slot === 'leads_pipeline') {
                if (pipelineConfigId == null) return
                const copied = await copyPipelineConfig.mutateAsync({
                    sourceConfigId: pipelineConfigId,
                    name: trimmed,
                })
                setPipelineConfigId(copied.id)
                syncBundleSlots({
                    leads_pipeline: {
                        configId: copied.id,
                        name: copied.name,
                        usedByOtherConfigs: 0,
                    },
                })
                setArtifactCopy(null)
                toast.success('Created a copy for this config.')
                return
            }

            const selected = currentSetupSelected(slot)
            if (!selected) return
            const packId = parseSetupPackId(selected.id)

            if (slot === 'applicant_data') {
                if (packId == null) {
                    toast.error('Could not copy this artifact for this config.')
                    return
                }
                const copied = await copyApplicantWorkbook.mutateAsync({
                    sourceWorkbookId: packId,
                    name: trimmed,
                })
                const forked: SelectedSetupConfig = {
                    id: String(copied.id),
                    name: copied.name,
                    source: 'existing',
                    usedByOtherConfigs: 0,
                    modifiedAt: copied.modified_date,
                }
                setSetupSelected(slot, forked)
                setPropertyCatalog((prev) => ({
                    ...prev,
                    applicant_data: ensureCatalogHas(
                        prev.applicant_data,
                        forked,
                    ),
                }))
                setArtifactCopy(null)
                toast.success('Created a copy for this config.')
                return
            }

            const path = pathByTab[slot]
            if (packId != null && path) {
                const copied = await copySetupPack.mutateAsync({
                    path,
                    packId,
                    name: trimmed,
                })
                const forked: SelectedSetupConfig = {
                    id: String(copied.id),
                    name: copied.name,
                    source: 'existing',
                    usedByOtherConfigs: 0,
                    modifiedAt: copied.modified_date,
                }
                setSetupSelected(slot, forked)
                setPropertyCatalog((prev) => ({
                    ...prev,
                    [slot]: ensureCatalogHas(prev[slot], forked),
                }))
                setArtifactCopy(null)
                toast.success('Created a copy for this config.')
                return
            }

            const forked = forkSetupSlot(selected, trimmed)
            forked.name = trimmed
            setSetupSelected(slot, forked)
            setPropertyCatalog((prev) => ({
                ...prev,
                [slot]: ensureCatalogHas(prev[slot], forked),
            }))
            setArtifactCopy(null)
            toast.success('Created a copy for this config.')
        } catch {
            toast.error('Could not copy this artifact for this config.')
        }
    }

    if (apartmentId == null) {
        return (
            <Box className={classes.centered}>
                <Typography color="textSecondary">
                    Select a property workspace to edit leasing season
                    configuration.
                </Typography>
            </Box>
        )
    }

    if (bundle == null) {
        const loadingDetail =
            bundleId != null &&
            (detailQuery.isLoading || detailQuery.isFetching)
        return (
            <Box className={classes.root}>
                <Box className={classes.header}>
                    <Box className={classes.titleRow}>
                        <Box className={classes.titleIcon}>
                            <AccountTreeIcon fontSize="small" />
                        </Box>
                        <Box className={classes.titleBlock}>
                            <Typography className={classes.pageTitle}>
                                Leasing Season Config
                            </Typography>
                            <Typography className={classes.pageSubtitle}>
                                Create or open a config bundle, then fill its
                                artifact slots. Seasons apply the whole config.
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                <Box className={classes.tabPanel}>
                    {loadingDetail ? (
                        <Box className={classes.centered}>
                            <Typography color="textSecondary">
                                Loading leasing season config…
                            </Typography>
                        </Box>
                    ) : (
                        <SeasonConfigBundleChooser
                            existing={existingOptions}
                            loading={listQuery.isLoading}
                            submitting={createMutation.isLoading}
                            onSelectExisting={(configId) => {
                                history.push(editorPath(configId))
                            }}
                            onCreate={(payload) => {
                                createMutation.mutate(payload, {
                                    onSuccess: (detail) => {
                                        applyDetail(detail, true)
                                    },
                                    onError: () => {
                                        toast.error(
                                            'Could not create leasing season config.',
                                        )
                                    },
                                })
                            }}
                        />
                    )}
                </Box>
            </Box>
        )
    }

    const seasonLocked =
        !!detailQuery.data?.active_bound_season ||
        detailQuery.data?.is_editable === false
    const contentLocked = seasonLocked || !!bundle.orgMandated

    const setupTabIndex = Math.max(
        0,
        SETUP_TABS.findIndex((tab) => tab.id === setupTab),
    )

    const setupHasSelection: Record<SetupTabId, boolean> = {
        applicant_data: applicantSelected != null,
        guest_card: guestCardSelected != null,
        application_fee: feeSelected != null,
        conditional_approvals: conditionalSelected != null,
        signable_docs: signableSelected != null,
        property_data: propertyDataSelected != null,
    }

    const activeSetupSelected = currentSetupSelected(setupTab)
    const activeUsedBy = artifactUsedByOthers(activeSetupSelected)
    const setupNeedsCopy = activeUsedBy > 0 && !activeSetupSelected?.orgMandated
    const pipelineUsedBy = bundle.slots.leads_pipeline.usedByOtherConfigs ?? 0
    const pipelineLockedBySeason = pipelineQuery.data?.is_editable === false
    const pipelineNeedsCopy =
        pipelineConfigId != null &&
        !bundle.slots.leads_pipeline.orgMandated &&
        (pipelineUsedBy > 0 || pipelineLockedBySeason)
    const activePackLockedBySeason = (() => {
        const detail = detailQuery.data
        if (!detail) return false
        switch (setupTab) {
            case 'guest_card':
                return detail.guest_card_pack?.is_editable === false
            case 'signable_docs':
                return detail.signable_doc_set?.is_editable === false
            case 'application_fee':
                return detail.fee_policy?.is_editable === false
            case 'conditional_approvals':
                return detail.conditional_approvals?.is_editable === false
            case 'property_data':
                return detail.property_data_pack?.is_editable === false
            case 'applicant_data':
                return applicantWorkbookQuery.data?.is_editable === false
            default:
                return false
        }
    })()
    const activePackEditable = !setupNeedsCopy
    const artifactCopyBusy =
        copySetupPack.isLoading ||
        copyApplicantWorkbook.isLoading ||
        copyPipelineConfig.isLoading
    const copyBannerLayout = {
        maxWidth: LEASING_SEASON_CONFIG_CONTENT_MAX_WIDTH,
        width: '100%',
        alignSelf: 'center' as const,
        padding: '0 8px',
        boxSizing: 'border-box' as const,
        flexShrink: 0,
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Box className={classes.titleRow}>
                    <Box className={classes.titleIcon}>
                        <AccountTreeIcon fontSize="small" />
                    </Box>
                    <Box className={classes.headerBundle}>
                        <SeasonConfigBundleBar
                            variant="pageHeader"
                            bundle={{
                                ...bundle,
                                isEditable: detailQuery.data?.is_editable,
                                activeBoundSeason:
                                    detailQuery.data?.active_bound_season ??
                                    null,
                                boundSeason:
                                    detailQuery.data?.bound_season ?? null,
                                slots: {
                                    ...bundle.slots,
                                    applicant_data: applicantSelected,
                                    guest_card: guestCardSelected,
                                    application_fee: feeSelected,
                                    conditional_approvals: conditionalSelected,
                                    signable_docs: signableSelected,
                                    property_data: propertyDataSelected,
                                    leads_pipeline: {
                                        ...bundle.slots.leads_pipeline,
                                        configId: pipelineConfigId,
                                        name:
                                            pipelineConfigId != null
                                                ? bundle.slots.leads_pipeline
                                                      .name ??
                                                  `Pipeline #${pipelineConfigId}`
                                                : null,
                                    },
                                },
                            }}
                            onChange={clearBundle}
                            onCreateCopy={
                                seasonLocked && !bundle.orgMandated
                                    ? () => setCopyDialogOpen(true)
                                    : undefined
                            }
                        />
                    </Box>
                </Box>
            </Box>

            <Box className={classes.navRegions}>
                <Box
                    className={`${classes.region} ${classes.regionSetup} ${
                        shellArea === 'setup' ? classes.regionActive : ''
                    }`}
                >
                    <Typography className={classes.regionLabel}>
                        Setup
                    </Typography>
                    <Tabs
                        className={classes.regionTabs}
                        value={shellArea === 'setup' ? setupTabIndex : false}
                        onChange={(_, value: number) => {
                            setShellArea('setup')
                            setSetupTab(SETUP_TABS[value].id)
                        }}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="standard"
                    >
                        {SETUP_TABS.map((tab) => (
                            <Tab
                                key={tab.id}
                                label={
                                    <span className={classes.tabLabel}>
                                        {tab.label}
                                        {!setupHasSelection[tab.id] ? (
                                            <span
                                                className={classes.tabBadge}
                                                title="No configuration selected"
                                            />
                                        ) : null}
                                    </span>
                                }
                            />
                        ))}
                    </Tabs>
                </Box>

                <Box
                    className={`${classes.region} ${classes.regionCompose} ${
                        shellArea === 'compose' ? classes.regionActive : ''
                    }`}
                >
                    <Typography className={classes.regionLabel}>
                        Compose
                    </Typography>
                    <Tabs
                        className={`${classes.regionTabs} ${classes.regionTabsCompose}`}
                        value={shellArea === 'compose' ? 0 : false}
                        onChange={() => setShellArea('compose')}
                        indicatorColor="primary"
                        textColor="primary"
                    >
                        <Tab
                            label={
                                <span className={classes.tabLabel}>
                                    Leads Pipeline
                                    {pipelineConfigId == null ? (
                                        <span
                                            className={classes.tabBadge}
                                            title="No configuration selected"
                                        />
                                    ) : null}
                                </span>
                            }
                        />
                    </Tabs>
                </Box>
            </Box>

            {shellArea === 'setup' ? (
                <Box className={classes.tabPanel}>
                    <Box className={classes.tabContent}>
                        {activeSetupSelected &&
                        setupNeedsCopy &&
                        !contentLocked ? (
                            <Box style={copyBannerLayout}>
                                <EditScopeBanner
                                    artifactLabel={
                                        SEASON_CONFIG_SLOT_META[setupTab].label
                                    }
                                    usedByOtherConfigs={activeUsedBy}
                                    lockedByActiveSeason={
                                        activePackLockedBySeason
                                    }
                                    copying={artifactCopyBusy}
                                    onCreateCopy={() =>
                                        openArtifactCopy(setupTab)
                                    }
                                />
                            </Box>
                        ) : null}

                        {setupTab === 'applicant_data' ? (
                            <ApplicantDataSetupTab
                                selected={applicantSelected}
                                onSelected={async (config) => {
                                    const numericId = parseSetupPackId(
                                        config.id,
                                    )
                                    if (numericId != null) {
                                        setSetupSelected(
                                            'applicant_data',
                                            config,
                                        )
                                        return
                                    }
                                    try {
                                        const created = await createApplicantWorkbook.mutateAsync(
                                            { name: config.name },
                                        )
                                        pinCreatedPack(
                                            'applicant_data',
                                            config,
                                            created,
                                        )
                                    } catch {
                                        toast.error(
                                            'Could not create applicant data.',
                                        )
                                    }
                                }}
                                onClearSelection={() => {
                                    if (!confirmDiscardPack('applicant_data'))
                                        return
                                    setSetupSelected('applicant_data', null)
                                }}
                                existing={propertyCatalog.applicant_data}
                                onExistingChange={setExistingFor(
                                    'applicant_data',
                                )}
                                sections={applicantSections}
                                onSectionsChange={(next) => {
                                    const synced = applyQualifyingMethodOptionsToSections(
                                        next,
                                        conditionalPolicy,
                                    )
                                    setApplicantSections(synced)
                                    markPackDirty('applicant_data')
                                }}
                                contentLocked={contentLocked}
                                artifactEditable={activePackEditable}
                                saveAction={packSaveAction(
                                    !contentLocked && activePackEditable,
                                    !!packDirty.applicant_data,
                                    saveApplicantWorkbook.isLoading,
                                    () =>
                                        persistExtras(
                                            {
                                                workbookSections: applicantSections,
                                            },
                                            () =>
                                                clearPackDirty(
                                                    'applicant_data',
                                                ),
                                        ),
                                )}
                                petsPolicy={
                                    propertyDataSelected
                                        ? propertyDataDraft.pets
                                        : undefined
                                }
                            />
                        ) : null}
                        {setupTab === 'guest_card' ? (
                            <GuestCardPackTab
                                selected={guestCardSelected}
                                onSelected={async (config) => {
                                    const numericId = parseSetupPackId(
                                        config.id,
                                    )
                                    if (numericId != null) {
                                        setSetupSelected('guest_card', config)
                                        const pack = guestPacksQuery.data?.find(
                                            (row) =>
                                                String(row.id) === config.id,
                                        )
                                        setGuestCardChannels(
                                            pack
                                                ? guestCardChannelBindingsFromPackDefinition(
                                                      pack.definition,
                                                  )
                                                : emptyGuestCardChannelBindings(),
                                        )
                                        return
                                    }
                                    try {
                                        const created = await createGuestCardPack.mutateAsync(
                                            {
                                                name: config.name,
                                                definition: guestCardPackDefinitionFromChannels(
                                                    emptyGuestCardChannelBindings(),
                                                ),
                                            },
                                        )
                                        pinCreatedPack(
                                            'guest_card',
                                            config,
                                            created,
                                        )
                                        setGuestCardChannels(
                                            guestCardChannelBindingsFromPackDefinition(
                                                created.definition,
                                            ),
                                        )
                                    } catch {
                                        toast.error(
                                            'Could not create guest card pack.',
                                        )
                                    }
                                }}
                                onClearSelection={() => {
                                    if (!confirmDiscardPack('guest_card'))
                                        return
                                    setSetupSelected('guest_card', null)
                                    setGuestCardChannels(
                                        emptyGuestCardChannelBindings(),
                                    )
                                }}
                                existing={propertyCatalog.guest_card}
                                onExistingChange={setExistingFor('guest_card')}
                                channelBindings={guestCardChannels}
                                onChannelBindingsChange={(next) => {
                                    setGuestCardChannels(next)
                                    markPackDirty('guest_card')
                                }}
                                contentLocked={contentLocked}
                                artifactEditable={activePackEditable}
                                saveAction={packSaveAction(
                                    !contentLocked && activePackEditable,
                                    !!packDirty.guest_card,
                                    saveGuestCardPack.isLoading,
                                    () =>
                                        persistExtras(
                                            {
                                                guestCardChannels: guestCardChannels,
                                            },
                                            () => clearPackDirty('guest_card'),
                                        ),
                                )}
                            />
                        ) : null}
                        {setupTab === 'application_fee' ? (
                            <ApplicationFeeTab
                                selected={feeSelected}
                                onSelected={async (config) => {
                                    const numericId = parseSetupPackId(
                                        config.id,
                                    )
                                    if (numericId != null) {
                                        setSetupSelected(
                                            'application_fee',
                                            config,
                                        )
                                        const policy = feePoliciesQuery.data?.find(
                                            (row) =>
                                                String(row.id) === config.id,
                                        )
                                        if (policy) {
                                            setFeeAmount(
                                                policy.amount ?? '50.00',
                                            )
                                            setFeeCurrency(
                                                policy.currency ?? 'USD',
                                            )
                                        }
                                        return
                                    }
                                    try {
                                        const created = await createFeePolicy.mutateAsync(
                                            {
                                                name: config.name,
                                                amount: feeAmount,
                                                currency: feeCurrency,
                                            },
                                        )
                                        pinCreatedPack(
                                            'application_fee',
                                            config,
                                            created,
                                        )
                                        setFeeAmount(
                                            created.amount ?? feeAmount,
                                        )
                                        setFeeCurrency(
                                            created.currency ?? feeCurrency,
                                        )
                                    } catch {
                                        toast.error(
                                            'Could not create application fee policy.',
                                        )
                                    }
                                }}
                                onClearSelection={() => {
                                    if (!confirmDiscardPack('application_fee'))
                                        return
                                    setSetupSelected('application_fee', null)
                                }}
                                existing={propertyCatalog.application_fee}
                                onExistingChange={setExistingFor(
                                    'application_fee',
                                )}
                                amount={feeAmount}
                                currency={feeCurrency}
                                onAmountChange={(next) => {
                                    setFeeAmount(next)
                                    markPackDirty('application_fee')
                                }}
                                contentLocked={contentLocked}
                                artifactEditable={activePackEditable}
                                saveAction={packSaveAction(
                                    !contentLocked && activePackEditable,
                                    !!packDirty.application_fee,
                                    saveFeePolicy.isLoading,
                                    () =>
                                        persistExtras(
                                            { feeAmount, feeCurrency },
                                            () =>
                                                clearPackDirty(
                                                    'application_fee',
                                                ),
                                        ),
                                )}
                            />
                        ) : null}
                        {setupTab === 'property_data' ? (
                            <PropertyDataSetupTab
                                selected={propertyDataSelected}
                                onSelected={async (config) => {
                                    const numericId = parseSetupPackId(
                                        config.id,
                                    )
                                    if (numericId != null) {
                                        setSetupSelected(
                                            'property_data',
                                            config,
                                        )
                                        const pack = propertyDataPacksQuery.data?.find(
                                            (row) =>
                                                String(row.id) === config.id,
                                        )
                                        setPropertyDataDraft(
                                            propertyDataFromPackDefinition(
                                                pack?.definition,
                                            ),
                                        )
                                        return
                                    }
                                    try {
                                        const created = await createPropertyDataPack.mutateAsync(
                                            {
                                                name: config.name,
                                                definition: propertyDataToPackDefinition(
                                                    emptyPropertyDataDraft(),
                                                ),
                                            },
                                        )
                                        const selected: SelectedSetupConfig = {
                                            ...config,
                                            id: String(created.id),
                                            source: 'existing',
                                        }
                                        setPropertyCatalog((prev) => ({
                                            ...prev,
                                            property_data: mergePackCatalog(
                                                prev.property_data.filter(
                                                    (row) =>
                                                        row.id !== config.id,
                                                ),
                                                [created],
                                            ),
                                        }))
                                        setSetupSelected(
                                            'property_data',
                                            selected,
                                        )
                                        setPropertyDataDraft(
                                            propertyDataFromPackDefinition(
                                                created.definition,
                                            ),
                                        )
                                    } catch {
                                        toast.error(
                                            'Could not create property data pack.',
                                        )
                                    }
                                }}
                                onClearSelection={() => {
                                    if (!confirmDiscardPack('property_data'))
                                        return
                                    setSetupSelected('property_data', null)
                                    setPropertyDataDraft(
                                        emptyPropertyDataDraft(),
                                    )
                                }}
                                existing={propertyCatalog.property_data}
                                onExistingChange={setExistingFor(
                                    'property_data',
                                )}
                                draft={propertyDataDraft}
                                onDraftChange={(next) => {
                                    setPropertyDataDraft(next)
                                    markPackDirty('property_data')
                                }}
                                contentLocked={contentLocked}
                                artifactEditable={activePackEditable}
                                saveAction={packSaveAction(
                                    !contentLocked && activePackEditable,
                                    !!packDirty.property_data,
                                    savePropertyDataPack.isLoading,
                                    () =>
                                        persistExtras(
                                            {
                                                propertyDataDraft,
                                            },
                                            () =>
                                                clearPackDirty('property_data'),
                                        ),
                                )}
                            />
                        ) : null}
                        {setupTab === 'conditional_approvals' ? (
                            <ConditionalApprovalsSetupTab
                                selected={conditionalSelected}
                                onSelected={async (config) => {
                                    const numericId = parseSetupPackId(
                                        config.id,
                                    )
                                    if (numericId != null) {
                                        setSetupSelected(
                                            'conditional_approvals',
                                            config,
                                        )
                                        const policy = conditionalListQuery.data?.find(
                                            (row) =>
                                                String(row.id) === config.id,
                                        )
                                        setConditionalPolicy(
                                            policy
                                                ? conditionalPolicyFromPackDefinition(
                                                      policy.definition,
                                                  )
                                                : defaultConditionalApprovalsPolicy(),
                                        )
                                        return
                                    }
                                    try {
                                        const created = await createConditionalApprovals.mutateAsync(
                                            {
                                                name: config.name,
                                                definition: (defaultConditionalApprovalsPolicy() as unknown) as Record<
                                                    string,
                                                    unknown
                                                >,
                                            },
                                        )
                                        pinCreatedPack(
                                            'conditional_approvals',
                                            config,
                                            created,
                                        )
                                        setConditionalPolicy(
                                            conditionalPolicyFromPackDefinition(
                                                created.definition,
                                            ),
                                        )
                                    } catch {
                                        toast.error(
                                            'Could not create qualifying methods.',
                                        )
                                    }
                                }}
                                onClearSelection={() => {
                                    if (
                                        !confirmDiscardPack(
                                            'conditional_approvals',
                                        )
                                    )
                                        return
                                    setSetupSelected(
                                        'conditional_approvals',
                                        null,
                                    )
                                }}
                                existing={propertyCatalog.conditional_approvals}
                                onExistingChange={setExistingFor(
                                    'conditional_approvals',
                                )}
                                policy={conditionalPolicy}
                                onPolicyChange={(next) => {
                                    setConditionalPolicy(next)
                                    markPackDirty('conditional_approvals')
                                    const syncedSections = applyQualifyingMethodOptionsToSections(
                                        applicantSections,
                                        next,
                                    )
                                    setApplicantSections(syncedSections)
                                    markPackDirty('applicant_data')
                                }}
                                contentLocked={contentLocked}
                                artifactEditable={activePackEditable}
                                saveAction={packSaveAction(
                                    !contentLocked && activePackEditable,
                                    !!packDirty.conditional_approvals,
                                    saveConditionalApprovals.isLoading,
                                    () =>
                                        persistExtras(
                                            {
                                                conditionalPolicy,
                                                workbookSections: applicantSections,
                                            },
                                            () => {
                                                clearPackDirty(
                                                    'conditional_approvals',
                                                )
                                                clearPackDirty('applicant_data')
                                            },
                                        ),
                                )}
                            />
                        ) : null}
                        {setupTab === 'signable_docs' ? (
                            <SignableDocsSetTab
                                selected={signableSelected}
                                onSelected={async (config) => {
                                    const numericId = parseSetupPackId(
                                        config.id,
                                    )
                                    if (numericId != null) {
                                        setSetupSelected(
                                            'signable_docs',
                                            config,
                                        )
                                        const docSet = signableSetsQuery.data?.find(
                                            (row) =>
                                                String(row.id) === config.id,
                                        )
                                        if (docSet) {
                                            setLeasePacket(
                                                leasePacketFromPackDefinition(
                                                    docSet.definition,
                                                ),
                                            )
                                            setGuarantorPacket(
                                                guarantorPacketFromPackDefinition(
                                                    docSet.definition,
                                                ),
                                            )
                                        } else {
                                            setLeasePacket(emptyLeasePacket())
                                            setGuarantorPacket(
                                                emptyGuarantorPacket(),
                                            )
                                        }
                                        return
                                    }
                                    try {
                                        const created = await createSignableDocSet.mutateAsync(
                                            {
                                                name: config.name,
                                                definition: signableDocSetDefinitionFromPackets(
                                                    leasePacket,
                                                    guarantorPacket,
                                                ),
                                            },
                                        )
                                        pinCreatedPack(
                                            'signable_docs',
                                            config,
                                            created,
                                        )
                                        setLeasePacket(
                                            leasePacketFromPackDefinition(
                                                created.definition,
                                            ),
                                        )
                                        setGuarantorPacket(
                                            guarantorPacketFromPackDefinition(
                                                created.definition,
                                            ),
                                        )
                                    } catch {
                                        toast.error(
                                            'Could not create signable doc set.',
                                        )
                                    }
                                }}
                                onClearSelection={() => {
                                    if (!confirmDiscardPack('signable_docs'))
                                        return
                                    setSetupSelected('signable_docs', null)
                                    setLeasePacket(emptyLeasePacket())
                                    setGuarantorPacket(emptyGuarantorPacket())
                                }}
                                existing={propertyCatalog.signable_docs}
                                onExistingChange={setExistingFor(
                                    'signable_docs',
                                )}
                                leasePacket={leasePacket}
                                onLeasePacketChange={(next) => {
                                    setLeasePacket(next)
                                    markPackDirty('signable_docs')
                                }}
                                guarantorPacket={guarantorPacket}
                                onGuarantorPacketChange={(next) => {
                                    setGuarantorPacket(next)
                                    markPackDirty('signable_docs')
                                }}
                                contentLocked={contentLocked}
                                artifactEditable={activePackEditable}
                                saveAction={packSaveAction(
                                    !contentLocked && activePackEditable,
                                    !!packDirty.signable_docs,
                                    saveSignableDocSet.isLoading,
                                    () =>
                                        persistExtras(
                                            {
                                                leasePacket,
                                                guarantorPacket,
                                            },
                                            () =>
                                                clearPackDirty('signable_docs'),
                                        ),
                                )}
                            />
                        ) : null}
                    </Box>
                </Box>
            ) : null}

            <Box
                className={classes.tabPanel}
                style={{
                    display: shellArea === 'compose' ? 'flex' : 'none',
                }}
            >
                {pipelineNeedsCopy && !contentLocked ? (
                    <Box style={copyBannerLayout}>
                        <EditScopeBanner
                            artifactLabel={
                                SEASON_CONFIG_SLOT_META.leads_pipeline.label
                            }
                            usedByOtherConfigs={pipelineUsedBy}
                            lockedByActiveSeason={pipelineLockedBySeason}
                            copying={artifactCopyBusy}
                            onCreateCopy={() =>
                                openArtifactCopy('leads_pipeline')
                            }
                        />
                    </Box>
                ) : null}
                <Box style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    <LeadsPipelineComposeTab
                        apartmentId={apartmentId}
                        configId={pipelineConfigId}
                        onConfigSelected={selectPipeline}
                        onClearSelection={clearPipeline}
                        workbookSections={applicantSections}
                        applicantDataSelected={applicantSelected != null}
                        conditionalApprovalsSelected={
                            conditionalSelected != null
                        }
                        conditionalApprovalsPolicy={conditionalPolicy}
                        signableDocsSelected={signableSelected != null}
                        leasePacket={leasePacket}
                        guarantorPacket={guarantorPacket}
                        feeAmount={feeAmount}
                        onDirtyChange={setTimelineDirty}
                        sharedWithOtherConfigs={pipelineUsedBy}
                        onCreateCopy={
                            pipelineNeedsCopy && !contentLocked
                                ? () => openArtifactCopy('leads_pipeline')
                                : undefined
                        }
                        copying={artifactCopyBusy}
                    />
                </Box>
            </Box>

            <NameConfigDialog
                open={artifactCopy != null}
                title={`Copy ${artifactCopy?.label ?? 'artifact'}`}
                message={`Create a copy of this ${
                    artifactCopy?.label ?? 'artifact'
                } for “${
                    bundle.name
                }”. Other leasing season configs keep the original.`}
                initialName={artifactCopy?.initialName ?? ''}
                confirmLabel="Create copy"
                submitting={artifactCopyBusy}
                onClose={() => {
                    if (!artifactCopyBusy) setArtifactCopy(null)
                }}
                onConfirm={(name) => {
                    void copyPinnedArtifact(name)
                }}
            />

            <NameConfigDialog
                open={copyDialogOpen}
                title="Copy to draft"
                message={
                    detailQuery.data?.bound_season?.name ||
                    detailQuery.data?.active_bound_season?.name
                        ? `“${bundle.name}” is used by ${
                              detailQuery.data.bound_season?.name ||
                              detailQuery.data.active_bound_season?.name
                          } and can’t be edited. Create a draft copy to start from for another season — this season keeps the original.`
                        : `“${bundle.name}” is bound to a season that has started, so it can’t be edited. Create a draft copy to start from for another season.`
                }
                initialName={`${bundle.name} (copy)`}
                confirmLabel="Create draft"
                submitting={copyMutation.isLoading}
                onClose={() => setCopyDialogOpen(false)}
                onConfirm={(name) => {
                    if (bundleId == null) return
                    copyMutation.mutate(
                        { sourceConfigId: bundleId, name },
                        {
                            onSuccess: (detail) => {
                                setCopyDialogOpen(false)
                                applyDetail(detail, true)
                                toast.success(
                                    'Created a draft copy of this config.',
                                )
                            },
                            onError: () => {
                                toast.error(
                                    'Could not copy leasing season config.',
                                )
                            },
                        },
                    )
                }}
            />
        </Box>
    )
}

/** @deprecated Prefer LeasingSeasonConfigEditorPage — same shell for root + detail. */
export const LeasingSeasonConfigChooserPage = LeasingSeasonConfigEditorPage
